import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../components/tailwind";
import { classNames } from "../../helpers";
import { useLocalStorage } from "../../hooks";
import { useToast } from "../../provider/ToastProvider";
import { dashboardState } from "../../redux/reducers/dashboardReducer";
import { setSectionLoading } from "../../redux/reducers/loadingReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForClasses } from "../../utils/confighelpers";
import ClassDrawer, { SHOW_INVITE_AND_PAY_AT_VENUE, CANCELLATION_OPTIONS, formatDuration, getSingleSessionPricing } from "./ClassDrawer";

function Icon({ d, className = "h-4 w-4" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {d}
    </svg>
  );
}

function ClassCard({ item, onEdit }) {
  const { capacity } = getSingleSessionPricing(item);
  const published = item.isClassPublic !== false && item.isPublic !== false;
  const cancel = item.classCancelationPolicy?.isEnabled
    ? CANCELLATION_OPTIONS.find((o) => o.minutes === item.classCancelationPolicy?.minutes)?.label || item.classCancelationPolicy?.text
    : null;

  return (
    <li className="group flex flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.06)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(72,27,58,0.12)]">
      <button type="button" onClick={onEdit} className="relative block aspect-[16/9] w-full overflow-hidden bg-[#F3E9FF] text-left" aria-label={`Edit ${item.className}`}>
        {item.classPhoto ? (
          <img src={item.classPhoto} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-4xl font-black text-indigo-bz">{(item.className || "C").slice(0, 1)}</span>
        )}
        <span
          className={classNames(
            "absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-extrabold shadow-sm",
            published ? "bg-white/95 text-bz-status-green" : "bg-white/95 text-gray-600"
          )}
        >
          {published ? "● Live" : "Draft"}
        </span>
        {SHOW_INVITE_AND_PAY_AT_VENUE && item.isPrivate && (
          <span className="absolute right-3 top-3 rounded-full bg-brown-coffee/90 px-2.5 py-1 text-xs font-extrabold text-white">Invite only</span>
        )}
      </button>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        {item.classType && <span className="self-start rounded-full bg-[#F3E9FF] px-2.5 py-0.5 text-xs font-extrabold text-indigo-bz">{item.classType}</span>}
        <h3 className="mt-2 truncate text-lg font-black tracking-tight text-gray-900">{item.className}</h3>
        {item.instructorName && <p className="mt-0.5 truncate text-sm text-gray-600">with {item.instructorName}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">
            <Icon className="h-4 w-4 text-portland-orange" d={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />
            {formatDuration(item.duration)}
          </span>
          {capacity ? (
            <span className="flex items-center gap-1.5">
              <Icon className="h-4 w-4 text-portland-orange" d={<><circle cx="9" cy="8" r="4" /><path d="M2 21a7 7 0 0 1 14 0M17 11a3 3 0 1 0 0-6M22 21a6 6 0 0 0-4-5.6" /></>} />
              {capacity} spots
            </span>
          ) : null}
        </div>
        <p className="mt-2 text-xs font-semibold text-gray-500">
          {cancel ? `Free cancellation up to ${cancel} before` : "No free cancellation"}
          {SHOW_INVITE_AND_PAY_AT_VENUE && item.paymentAtVenue ? " · Pay at venue" : ""}
        </p>
        <div className="mt-4 flex gap-2 pt-1">
          <button
            type="button"
            onClick={onEdit}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-extrabold text-gray-900 hover:bg-gray-50"
          >
            <Icon d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>} />
            Edit
          </button>
        </div>
      </div>
    </li>
  );
}

export default function ClassList({ navigation, profileFor, title, openNew = false }) {
  const { addSuccessToast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dashboardReduxState = useSelector(dashboardState);
  const [userToken] = useLocalStorage("userToken");
  const userReduxState = useSelector(userState);
  const [classes, setClasses] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [drawer, setDrawer] = React.useState({ open: !!openNew, classData: null });

  React.useEffect(() => {
    dispatch(setSectionLoading(false)); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (openNew) setDrawer({ open: true, classData: null });
  }, [openNew]);

  const getListOfClass = React.useCallback(async () => {
    try {
      const response = await axiosInstance({
        method: "get",
        url: `${getUrlForClasses()}listOfClasses?isPackage=${false}&listFor=${userReduxState.businessType}&onlyClassDetail=true`,
      });
      if (response.status === 200 && Array.isArray(response.data?.data)) {
        const list = response.data.data.filter((c) => !c.isDeleted).slice().reverse();
        setClasses(list);
        return list;
      }
    } catch (e) {
      // keep whatever we had
    } finally {
      setLoaded(true);
    }
    return null;
  }, [userReduxState.businessType]);

  React.useEffect(() => {
    getListOfClass();
  }, [userToken, dashboardReduxState.updateSessionComp, getListOfClass]);

  const closeDrawer = () => {
    setDrawer((d) => ({ ...d, open: false }));
    if (openNew) navigate("/myclasses", { replace: true });
  };

  const filtered = query.trim()
    ? classes.filter((c) => `${c.className} ${c.classType}`.toLowerCase().includes(query.trim().toLowerCase()))
    : classes;

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div className="w-full">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">{title || "My Classes"}</h1>
                <p className="mt-1 max-w-xl text-sm text-gray-600 sm:text-base">
                  Your class catalogue: what you offer, who teaches it and the booking rules. Members book with a credit pass or subscription. Add them to your timetable in{" "}
                  <button type="button" onClick={() => navigate("/myclasses/myschedule")} className="font-bold text-indigo-bz hover:text-indigo-bz-hover">
                    My Schedule
                  </button>
                  .
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawer({ open: true, classData: null })}
                className="flex items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-5 py-3 text-base font-extrabold text-white shadow-[0_10px_24px_rgba(72,27,58,0.25)] hover:bg-light-brown-coffee"
              >
                <Icon className="h-5 w-5" d={<path d="M12 5v14M5 12h14" />} />
                New class
              </button>
            </div>

            {classes.length > 3 && (
              <div className="relative mt-6 max-w-md">
                <Icon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search classes"
                  aria-label="Search classes"
                  className="block w-full rounded-2xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-base outline-none focus:border-indigo-bz focus:ring-4 focus:ring-[#F3E9FF]"
                />
              </div>
            )}

            {!loaded ? (
              <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
                    <div className="aspect-[16/9] animate-pulse bg-gray-100" />
                    <div className="space-y-3 p-5">
                      <div className="h-4 w-16 animate-pulse rounded-full bg-gray-100" />
                      <div className="h-5 w-2/3 animate-pulse rounded-full bg-gray-100" />
                      <div className="h-4 w-1/2 animate-pulse rounded-full bg-gray-100" />
                    </div>
                  </li>
                ))}
              </ul>
            ) : classes.length === 0 ? (
              <div className="mt-8 flex flex-col items-center rounded-3xl border border-dashed border-[#E3D0F7] bg-white px-6 py-14 text-center">
                <span className="fc-bob flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                  <Icon className="h-8 w-8" d={<><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" /></>} />
                </span>
                <h2 className="mt-5 text-xl font-black text-gray-900">Add your first class</h2>
                <p className="mt-2 max-w-md text-sm text-gray-600 sm:text-base">
                  Start with the basics — name, photo, duration and price. You'll put it on your timetable right after.
                </p>
                <button
                  type="button"
                  onClick={() => setDrawer({ open: true, classData: null })}
                  className="mt-6 rounded-2xl bg-brown-coffee px-6 py-3.5 text-base font-extrabold text-white hover:bg-light-brown-coffee"
                >
                  Create a class
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <p className="mt-8 text-center text-sm text-gray-600">No classes match “{query}”.</p>
            ) : (
              <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((item) => (
                  <ClassCard
                    key={item._id}
                    item={item}
                    onEdit={() => setDrawer({ open: true, classData: item })}
                  />
                ))}
              </ul>
            )}
          </div>

          <ClassDrawer
            open={drawer.open}
            classData={drawer.classData}
            onClose={closeDrawer}
            onSaved={async (saved, { isEdit }) => {
              await getListOfClass();
              if (isEdit) addSuccessToast("Class updated");
            }}
            onDeleted={async () => {
              await getListOfClass();
              addSuccessToast("Class deleted");
            }}
            onSchedule={() => {
              setDrawer({ open: false, classData: null });
              navigate("/myclasses/myschedule");
            }}
          />
        </div>
      }
    />
  );
}
